import { Observable } from 'rxjs';
import { Cacheable } from 'ts-cacheable';

import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ApiUrlInjectionToken, PriceTypeEnum, UnitsUpdateData } from '@mitsubishi/shared/data-access';
import { getRequestTraceHttpHeaders } from '@mitsubishi/shared/request-tracing';

import { Probability, SelectionStateUpdateEvent } from '../enums';
import { OfferRecipient, Selection, StatisticData, StatisticDataArray, UpdateOrderNotesRequest } from '../models';
import { PriceChanges } from '../models/price-changes.model';

@Injectable({
  providedIn: 'root',
})
export class SelectionService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl + '/project';
  }

  createQuote(projectId: string, organizationId: number): Observable<{ uuid: string }> {
    const params = new HttpParams().set('organizationId', organizationId);

    return this.http.post<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/selection`,
      {},
      {
        params,
      }
    );
  }

  duplicateQuote(projectId: string, selectionId: string): Observable<{ uuid: string }> {
    return this.http.post<{ uuid: string }>(`${this.baseUrl}/${projectId}/selection/${selectionId}/duplicate`, {});
  }

  deleteQuote(projectId: string, selectionId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}`, {});
  }

  /** @deprecated Use {@link patchSelection} instead */
  updateQuote(projectId: string, selectionId: string, title: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}`, { title });
  }

  patchSelection(
    projectId: string,
    selectionId: string,
    patch: Partial<{ title: string; notes: string }>
  ): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}`, patch);
  }

  updateCurrencyCode(
    projectId: string,
    selectionId: string,
    currencyCode: string,
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/currency`,
      { currencyCode },
      { headers }
    );
  }

  updateCurrencyMultiplier(
    projectId: string,
    selectionId: string,
    currencyMultiplier: number | null
  ): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/currencyMultiplier`, {
      currencyMultiplier,
    });
  }

  updateGlobalPriceMultiplier(projectId: string, selectionId: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/use-price-multiplier`, {});
  }

  updateAlternativePriceMultiplier(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId?: string,
    priceMultiplier?: number | null
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/price-multiplier`,
      {
        priceMultiplier,
        alternativeId,
      }
    );
  }

  updateCustomAlternativePriceMultiplier(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId?: string,
    priceMultiplier?: number | null
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/price-multiplier`,
      {
        priceMultiplier,
        alternativeId,
      }
    );
  }

  updatePriceMultiplier(projectId: string, selectionId: string, priceMultiplier: number): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/priceMultiplier`, {
      priceMultiplier,
    });
  }

  updateCustomCurrency(projectId: string, selectionId: string, customCurrency: string | null): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/customCurrency`, {
      customCurrency,
    });
  }

  toggleUseCustomCurrency(projectId: string, selectionId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/useCustomCurrency`, {});
  }

  toggleIsHot(projectId: string, selectionId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/hot`, {});
  }

  /** @deprecated Use {@link patchSelection} instead */
  editNote(projectId: string, selectionId: string, notes: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/notes`, { notes });
  }

  editAlternativeDescription(
    projectId: string,
    selectionId: string,
    alternativeId: string,
    notes: string
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/alternative/${alternativeId}`,
      notes ?? null
    );
  }

  updateOrderFields(
    projectId: string,
    selectionId: string,
    params: Partial<{
      deliveryDate: string | null;
      recipient: string;
      recipient2: string;
      street: string;
      city: string;
      country: string;
      orderNumber: string;
      job: string;
      zipCode: string;
      notes: UpdateOrderNotesRequest;
    }>
  ): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/order`, params);
  }

  updateOfferRecipient(projectId: string, selectionId: string, recipient: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/offer-recipient`, { recipient });
  }

  updateOfferAddress(projectId: string, selectionId: string, recipientAddress: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/offer-recipient-address`, {
      recipientAddress,
    });
  }

  shareOffer(
    projectId: string,
    selectionId: string,
    body: {
      email: string[];
      priceVisibility: string;
      priceType: string;
      noteVisibility: string;
      datasheetVisibility: string;
      documentVisibility: string;
    }
  ): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/share`, body);
  }

  exportOfferCsv(selectionId: string, priceType?: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('price', priceType ?? '');

    return this.http.get(`${this.baseUrl}/selection/${selectionId}/export`, {
      params,
      observe: 'response',
      responseType: 'blob',
    });
  }

  getQuotesTemplate(projectId: string, selectionId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${projectId}/selection/${selectionId}/template-quote`);
  }

  addAdditionalInfo(
    projectId: string,
    selectionId: string,
    title: string,
    description: string,
    inEvidence: boolean,
    categoryId: string,
    templateId: string
  ): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/offer-attachment`, {
      title,
      description,
      inEvidence,
      categoryId,
      templateId,
    });
  }

  updateAdditionalInfo(
    projectId: string,
    selectionId: string,
    additionalInfoId: string,
    title: string,
    description: string,
    inEvidence: boolean,
    categoryId: string,
    templateId: string
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/offer-attachment/${additionalInfoId}`,
      {
        title,
        description,
        inEvidence,
        categoryId,
        templateId,
      }
    );
  }

  deleteAdditionalInfo(projectId: string, selectionId: string, additionalInfoId: string): Observable<null> {
    return this.http.delete<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/offer-attachment/${additionalInfoId}`
    );
  }

  setStatisticData(projectId: string, selectionId: string, statisticData: StatisticDataArray[]): Observable<null> {
    return this.http.post<any>(`${this.baseUrl}/${projectId}/selection/${selectionId}/statistic-data`, statisticData);
  }

  getPriceChanges(selectionId: string): Observable<PriceChanges> {
    return this.http.get<PriceChanges>(`${this.baseUrl}/selection/${selectionId}/pricing-change`);
  }

  putPriceValidityDate(projectId: string, selectionId: string, priceValidityDate: string | null): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/dateUpdateSelection`, {
      priceValidityDate,
    });
  }

  toggleTestPriceList(projectId: string, selectionId: string): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/toggle-test-price-list`, null);
  }

  putExpiringDate(projectId: string, selectionId: string, expiringDate: string | null): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/offer-expiring-date`, {
      expiringDate,
    });
  }

  @Cacheable()
  getStatisticData(): Observable<StatisticData[]> {
    return this.http.get<StatisticData[]>(`${this.baseUrl}/selection/statistic-data`, {});
  }

  updateSelectionState(
    projectId: string,
    selectionId: string,
    state: SelectionStateUpdateEvent,
    additionalData?: Record<string, unknown>
  ): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/state`, {
      event: state,
      ...(additionalData ?? {}),
    });
  }

  downloadOrderLog(projectId: string, selectionId: string): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/sap-oreder-request`, {
      sendImmediate: true,
    });
  }

  downloadOrderXlsLog(projectId: string, selectionId: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/sap-oreder-request-xls`,
      {
        sendImmediate: true,
      },
      { responseType: 'blob' }
    );
  }

  updateOfferProbability(projectId: string, selectionId: string, probability: Probability): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/offer-probability`, {
      probability,
    });
  }

  checkModelsHasUpgrade(projectId: string, selectionId: string): Observable<UnitsUpdateData> {
    return this.http.get<UnitsUpdateData>(`${this.baseUrl}/${projectId}/selection/${selectionId}/check-upgrade`);
  }

  upgradeModels(projectId: string, selectionId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/upgrade`, {});
  }

  loadOfferRecipients(selectionId: string): Observable<OfferRecipient[]> {
    return this.http.get<OfferRecipient[]>(`${this.baseUrl}/selection/${selectionId}/recipients`);
  }

  refreshPrice(projectId: string, selectionId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/refresh-prices`, {});
  }

  setIsImportant(projectId: string, selectionId: string, documentId: string): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/document/${documentId}/is-important`,
      {}
    );
  }

  getSharedSelection(
    selectionId: string,
    body: {
      priceVisibility: string;
      priceType: string;
      noteVisibility: string;
      datasheetVisibility: string;
      documentVisibility: string;
    }
  ): Observable<Selection> {
    return this.http.post<Selection>(`${this.baseUrl}/selection/${selectionId}/preview`, body);
  }

  getOfferPdfFileUrl(
    selectionId: string,
    body: {
      priceVisibility: string;
      priceType: string;
      noteVisibility: string;
      datasheetVisibility: string;
      documentVisibility: string;
    }
  ) {
    return this.http.post(`${this.baseUrl}/selection/${selectionId}/offer/pdf`, body, { responseType: 'text' });
  }

  downloadOrderPDF(
    selectionId: string,
    productionSiteCode?: string,
    priceType?: string | PriceTypeEnum
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (priceType) {
      params = params.set('priceType', priceType);
    }

    if (productionSiteCode) {
      return this.http.get(`${this.baseUrl}/selection/${selectionId}/productionSite/${productionSiteCode}/order/pdf`, {
        observe: 'response',
        responseType: 'blob',
        params,
      });
    }

    return this.http.get(`${this.baseUrl}/selection/${selectionId}/order/pdf`, {
      observe: 'response',
      responseType: 'blob',
      params,
    });
  }

  deleteUnits(projectId: string, selectionId: string, alternativeId: string, unitIds: string[]) {
    return this.http.delete<void>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/alternative/${alternativeId}/delete-unit`,
      {
        body: { unitIds },
      }
    );
  }
}
